import throttle from 'lodash/throttle';
import React, { useEffect, useRef, useState } from 'react';
import { ReadMoreCommonProps } from './common';
import { DisplayAmount, DISPLAY_AMOUNT_HEIGHT_MAP } from './constants';
import { ReadMoreBase } from './ReadMoreBase';
import { ToggleButton } from './ToggleButton';
import { getToggleButtons } from './utils';

/**
 * Renders content that is collapsed when taller than the allowed height.
 */
export const FrameBasedReadMore: React.FunctionComponent<
    ReadMoreCommonProps
> = ({
    children,
    collapsedDisplayAmount = DisplayAmount.none,
    collapsedLinkText,
    defaultIsExpanded = false,
    expandedLinkText,
    onToggleExpanded,
    toggleLocation,
    customToggleClassNames,
}) => {
    const [isExpanded, setIsExpanded] = useState<boolean>(defaultIsExpanded);
    const contentDivRef = useRef<HTMLDivElement>(null);
    const [shouldTruncateContent, setShouldTruncateContent] =
        useState<boolean>(false);

    const handleClick = () => {
        const nextExpanded = !isExpanded;
        setIsExpanded(nextExpanded);
        onToggleExpanded?.(nextExpanded);
    };

    useEffect(() => {
        const updateShouldTruncateContent = (): void => {
            const contentDivHeight =
                contentDivRef.current?.offsetHeight ?? null;

            if (
                collapsedDisplayAmount === DisplayAmount.none ||
                !contentDivHeight
            ) {
                return;
            }

            setShouldTruncateContent(
                contentDivHeight >
                    DISPLAY_AMOUNT_HEIGHT_MAP[collapsedDisplayAmount],
            );
        };

        updateShouldTruncateContent();

        const handleTruncateOnWindowResize = throttle(
            updateShouldTruncateContent,
            250,
        );

        window.addEventListener('resize', handleTruncateOnWindowResize);

        return () => {
            window.removeEventListener('resize', handleTruncateOnWindowResize);
        };
    }, [collapsedDisplayAmount]);

    const toggleButton = (
        <ToggleButton
            collapsedLinkText={collapsedLinkText}
            customClassNames={customToggleClassNames}
            expandedLinkText={expandedLinkText}
            isExpanded={isExpanded}
            onClick={handleClick}
        />
    );

    const { topButton, bottomButton } = getToggleButtons({
        button: toggleButton,
        shouldTruncateContent,
        toggleLocation,
    });

    return (
        <>
            {topButton}
            <ReadMoreBase
                className="eds-read-more--fade"
                isExpanded={isExpanded}
                shouldTruncateContent={shouldTruncateContent}
                collapsedDisplayAmount={collapsedDisplayAmount}
                ref={contentDivRef}
            >
                {children}
            </ReadMoreBase>
            {bottomButton}
        </>
    );
};
